<template>
  <div class="wr-card">
  <div
    class="wr-entry"
    :class="{ 'wr-entry--expanded': expanded }"
    @click="expanded = !expanded"
  >
    <!-- Ícone -->
    <div class="wr-entry__icon-col">
      <div class="wr-entry__icon-wrap" :style="{ background: statusDisplay.bgColor, color: statusDisplay.color }">
        <i :class="['fas', statusDisplay.icon]"></i>
      </div>
    </div>

    <!-- Conteúdo -->
    <div class="wr-entry__content">
      <!-- Linha principal -->
      <div class="wr-entry__header">
        <span class="wr-entry__label">Saque #{{ formatShortId(request.id) }}</span>
        <div class="wr-entry__header-right">
          <span class="wr-entry__net" :style="{ color: netColor }">
            {{ formatCurrency(netAmount) }}
          </span>
          <i class="fas fa-chevron-down wr-entry__chevron" :class="{ 'wr-entry__chevron--open': expanded }"></i>
        </div>
      </div>

      <!-- Meta: data + badge -->
      <div class="wr-entry__meta">
        <span class="wr-entry__date">{{ formatDate(request.requestDate) }}</span>
        <span
          class="wr-entry__badge"
          :style="{ color: statusDisplay.color, backgroundColor: statusDisplay.bgColor }"
        >
          <i :class="['fas', statusDisplay.icon, 'me-1']" style="font-size:0.65rem;"></i>
          {{ statusDisplay.label }}
        </span>
      </div>

      <!-- Detalhe expandido -->
      <Transition name="wr-fees">
        <div v-if="expanded" class="wr-entry__fees">
          <div v-if="request.event" class="wr-entry__fee-row">
            <span class="text-muted">Evento</span>
            <span>{{ request.event.name }}</span>
          </div>
          <div class="wr-entry__fee-row">
            <span class="text-muted">Valor bruto</span>
            <span>{{ formatCurrency(request.amount) }}</span>
          </div>
          <div v-if="request.transfer_fee > 0" class="wr-entry__fee-row">
            <span class="text-muted">Taxa de transferência</span>
            <span class="text-danger">−{{ formatCurrency(request.transfer_fee) }}</span>
          </div>
          <div class="wr-entry__fee-row wr-entry__fee-row--highlight">
            <span>Você vai receber</span>
            <span :style="{ color: netColor, fontWeight: 600 }">{{ formatCurrency(netAmount) }}</span>
          </div>
          <div v-if="request.pixKeyType" class="wr-entry__fee-row">
            <span class="text-muted">Chave Pix</span>
            <span>{{ formatPixKeyType(request.pixKeyType) }}</span>
          </div>
          <div v-if="safeStatusMessage" class="wr-entry__alert mt-2">
            <i class="fas fa-exclamation-circle me-1"></i> {{ safeStatusMessage }}
          </div>
        </div>
      </Transition>
    </div>
  </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { getWithdrawalStatusDisplay } from '@/utils/withdrawalStatus';
import { formatBRL as formatCurrency } from '@/utils/currencyFormatter';
import { formatDateTime as formatDate } from '@/utils/dateFormatter';
import { pixKeyTypeLabel as formatPixKeyType } from '@/utils/pixHelpers';

const props = defineProps({
  request: {
    type: Object,
    required: true,
  },
});

const expanded = ref(false);

const statusDisplay = computed(() => getWithdrawalStatusDisplay(props.request.status));

const technicalStatusMessagePattern = /D\+3|asaas|stripe|gateway|provider|payout|ledger|webhook|kyc/i;
const genericStatusMessage = 'Não foi possível concluir esta transferência. Nossa equipe está acompanhando o caso.';

const safeStatusMessage = computed(() => {
  const message = typeof props.request.statusMessage === 'string'
    ? props.request.statusMessage.trim()
    : '';

  if (!message) return '';
  return technicalStatusMessagePattern.test(message) ? genericStatusMessage : message;
});

const netAmount = computed(() => Math.max((props.request.amount ?? 0) - (props.request.transfer_fee ?? 0), 0));

const netColor = computed(() => {
  if (props.request.status === 'completed') return '#10b981';
  if (props.request.status === 'failed' || props.request.status === 'cancelled') return '#ef4444';
  return '#3b82f6';
});

const formatShortId = (uuid) => uuid ? uuid.substring(0, 6).toUpperCase() : 'N/A';
</script>

<style scoped>
.wr-card {
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e8edf2;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
  overflow: hidden;
}

.wr-entry {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  cursor: pointer;
  transition: background 0.15s;
  border-radius: 12px;
}
.wr-entry:hover { background: #f8fafc; }

.wr-entry__icon-col { flex-shrink: 0; padding-top: 2px; }
.wr-entry__icon-wrap {
  width: 36px; height: 36px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.85rem;
}

.wr-entry__content { flex: 1; min-width: 0; }

.wr-entry__header {
  display: flex; align-items: center; justify-content: space-between;
  gap: 0.5rem;
}
.wr-entry__label { font-size: 0.875rem; font-weight: 500; color: #1e293b; }
.wr-entry__header-right { display: flex; align-items: center; gap: 0.5rem; }
.wr-entry__net { font-size: 0.875rem; font-weight: 600; }

.wr-entry__chevron {
  font-size: 0.7rem; color: #94a3b8;
  transition: transform 0.2s;
}
.wr-entry__chevron--open { transform: rotate(180deg); }

.wr-entry__meta {
  display: flex; align-items: center; gap: 0.5rem;
  margin-top: 3px;
}
.wr-entry__date { font-size: 0.75rem; color: #94a3b8; }
.wr-entry__badge {
  font-size: 0.68rem; font-weight: 600;
  padding: 2px 7px; border-radius: 20px;
}

.wr-entry__fees {
  margin-top: 0.6rem;
  padding: 0.6rem 0.75rem;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e8edf2;
}
.wr-entry__fee-row {
  display: flex; justify-content: space-between;
  font-size: 0.8rem; padding: 3px 0;
  color: #475569;
}
.wr-entry__fee-row--highlight {
  border-top: 1px solid #e2e8f0;
  margin-top: 4px; padding-top: 6px;
  font-size: 0.85rem;
}
.wr-entry__alert {
  font-size: 0.8rem; color: #664d03;
  background: #fff3cd; border-radius: 6px;
  padding: 6px 10px;
}

.wr-fees-enter-active, .wr-fees-leave-active { transition: all 0.2s ease; }
.wr-fees-enter-from, .wr-fees-leave-to { opacity: 0; transform: translateY(-4px); }
</style>
